import { DialogComponent } from '@theme/dialog';
import { fetchReviews, fillRatingDots, formatScore, getLocale, REVIEWS_PER_PAGE } from '@theme/product-reviews';

/**
 * @typedef {import('@theme/product-reviews').Review} Review
 * @typedef {import('@theme/product-reviews').ReviewsPage} ReviewsPage
 */

/** Pages loaded on the first open, before the shopper asks for more. */
const PAGES_ON_OPEN = 2;

/**
 * Lists a product's Yotpo reviews. The first open loads two pages; later pages load one at a time on request, moving
 * focus to the first new review. A combined listing sibling brings another product id, which starts the list over.
 *
 * @extends {DialogComponent}
 */
class ProductReviewsDialog extends DialogComponent {
  requiredRefs = ['dialog', 'summary', 'score', 'total', 'list', 'status', 'loadMore', 'reviewTemplate'];

  /** The product the list holds reviews for. */
  #productId = '';

  /** The pages of it loaded so far. */
  #pageCount = 0;

  /** The review count, from the first page. */
  #total = 0;

  #isComplete = false;

  /** @type {symbol | null} The load in progress, dropped when the list starts over. */
  #load = null;

  /** @type {Intl.DateTimeFormat | undefined} */
  #dateFormat;

  showDialog() {
    super.showDialog();

    if (this.dataset.productId !== this.#productId) this.#startOver();
    if (this.#pageCount === 0) this.#loadPages(PAGES_ON_OPEN);
  }

  loadMore = () => {
    // After a failed first open, the retry loads what the open would have
    this.#loadPages(this.#pageCount === 0 ? PAGES_ON_OPEN : 1, true);
  };

  #startOver() {
    const { summary, list, loadMore } = this.refs;

    this.#productId = this.dataset.productId ?? '';
    this.#pageCount = 0;
    this.#total = 0;
    this.#isComplete = false;
    this.#load = null;

    summary.hidden = true;
    list.replaceChildren();
    loadMore.hidden = true;
    this.#setStatus('');
  }

  /**
   * @param {number} count - How many pages to load, one after another.
   * @param {boolean} [focusNew] - Moves focus to the first review loaded.
   */
  async #loadPages(count, focusNew = false) {
    if (this.#load || this.#isComplete) return;

    const load = Symbol('load');
    const { list, loadMore } = this.refs;
    const appKey = this.dataset.appKey ?? '';
    /** @type {HTMLElement | null} */
    let firstNew = null;

    this.#load = load;
    loadMore.hidden = true;
    this.#setStatus(this.dataset.loadingLabel);

    try {
      for (let index = 0; index < count && !this.#isComplete; index++) {
        const page = await fetchReviews(appKey, this.#productId, this.#pageCount + 1);
        if (this.#load !== load) return;

        this.#pageCount += 1;
        if (this.#pageCount === 1) this.#renderSummary(page);

        const reviews = page.reviews.map(this.#renderReview);
        firstNew ??= reviews[0] ?? null;
        list.append(...reviews);

        // The first page's total is the count; a short page ends the list even when that total drifts
        this.#isComplete = page.reviews.length < REVIEWS_PER_PAGE || list.childElementCount >= this.#total;
      }
    } catch (error) {
      if (this.#load !== load) return;

      console.warn('[product-reviews-dialog] Reviews request failed:', error);
      this.#load = null;
      this.#setStatus(this.dataset.errorLabel);
      loadMore.hidden = false;
      return;
    }

    this.#load = null;
    loadMore.hidden = this.#isComplete;

    if (list.childElementCount === 0) {
      this.#setStatus(this.dataset.emptyLabel);
    } else {
      this.#setStatus(this.#isComplete ? this.dataset.endLabel : '');
    }

    if (!focusNew) return;

    const target = firstNew?.querySelector('[data-review-title]') ?? this.refs.status;
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * @param {ReviewsPage} page - The first page.
   */
  #renderSummary({ score, total }) {
    const { summary, score: scoreElement, total: totalElement } = this.refs;
    const dots = summary.querySelector('[data-rating-dots]');

    this.#total = total;
    if (total === 0) return;

    scoreElement.textContent = formatScore(score);
    if (dots) fillRatingDots(dots, score);

    const label = new Intl.PluralRules(getLocale()).select(total) === 'one' ? this.dataset.totalOne : this.dataset.totalOther;
    totalElement.textContent = (label ?? '').replace('[count]', total.toLocaleString(getLocale()));
    summary.hidden = false;
  }

  /**
   * @param {Review} review
   * @returns {HTMLElement}
   */
  #renderReview = (review) => {
    const fragment = /** @type {DocumentFragment} */ (this.refs.reviewTemplate.content.cloneNode(true));
    const item = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const date = new Date(review.createdAt);
    const score = formatScore(review.score);

    /** @param {string} name @param {string} text */
    const fill = (name, text) => {
      const element = item.querySelector(`[data-review-${name}]`);
      if (element) element.textContent = text;
    };

    if (!Number.isNaN(date.getTime())) {
      item.querySelector('time')?.setAttribute('datetime', review.createdAt);
      this.#dateFormat ??= new Intl.DateTimeFormat(getLocale(), { month: 'long', year: 'numeric' });
      fill('date', this.#dateFormat.format(date));
    }

    fill('title', review.title);
    fill('score', score);
    fill('score-label', (this.dataset.scoreLabel ?? '').replace('[score]', score));
    fill('content', review.content);
    fill('author', review.author);

    const dots = item.querySelector('[data-rating-dots]');
    if (dots) fillRatingDots(dots, review.score);

    return item;
  };

  /**
   * @param {string} [message]
   */
  #setStatus(message = '') {
    this.refs.status.textContent = message;
  }
}

if (!customElements.get('product-reviews-dialog')) {
  customElements.define('product-reviews-dialog', ProductReviewsDialog);
}
